import axios from "../util/axios";
import { BFF_URL } from "../util/config";

/** Avoid "Bearer Bearer …" if token was stored with a prefix. */
const bearerHeader = (token) => {
  if (token == null || token === "") return null;
  const t = String(token).trim();
  const raw = t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
  return raw ? `Bearer ${raw}` : null;
};

const authHeaders = (token) => {
  const auth = bearerHeader(token);
  return auth ? { Authorization: auth } : {};
};

// Public menu (no JWT) — browse before table code
export const fetchPublicMenu = async (restaurantId) => {
  const response = await axios.get(
    `${BFF_URL}/api/public/menu/${restaurantId}`
  );
  return response.data;
};

// Validate OPM token via BFF → restaurant API
export const validateToken = async (token) => {
  const response = await axios.get(`${BFF_URL}/api/validate-token/${token}`);
  return response.data;
};

/** GET dining session line items (BFF fixes closed=false; only lang is forwarded). */
export const fetchDiningSessionOrderItems = async (
  restaurantId,
  diningSessionId,
  token,
  lang = "he"
) => {
  const response = await axios.get(
    `${BFF_URL}/api/opm/restaurants/${restaurantId}/dining-sessions/${diningSessionId}`,
    {
      params: { lang },
      headers: authHeaders(token),
    }
  );
  return response.data;
};

// QR that opens OPM for a restaurant (?restaurant= on OPM).
export const getQRCode = async (restaurantId) => {
  const response = await axios.get(`${BFF_URL}/api/qr`, { params: { restaurantId } });
  return response.data;
};

// Add an order to the table the guest's OPM token belongs to (the session the
// waiter opened). Only items are sent; the session's details stay as the waiter set them.
export const addOrderToDiningSession = async (
  restaurantId,
  diningSessionId,
  orderData,
  token,
  lang = "he"
) => {
  const response = await axios.post(
    `${BFF_URL}/api/opm/restaurants/${restaurantId}/dining-sessions/${diningSessionId}/items`,
    orderData,
    { params: { lang }, headers: authHeaders(token) }
  );
  return response.data;
};

export const verifyOpmAccessCode = async (restaurantId, code) => {
  const response = await axios.post(
    `${BFF_URL}/api/auth/opm/${restaurantId}`,
    { code }
  );
  return response.data;
};

// ─── Web payment: the guest pays on the acquirer's hosted page ───────────────
// The BFF binds each call to a fixed shape: only these fields are sent, and only the
// fields the screens use come back. The guest picks how to pay on the page itself.

/** Opens the payment page for the guest's share. Returns { unique_id, session_id, session_url, token, signature }. */
export const createWebPayment = async (restaurantId, diningSessionId, payment, token) => {
  const response = await axios.post(
    `${BFF_URL}/api/opm/restaurants/${restaurantId}/dining-sessions/${diningSessionId}/web-payments`,
    {
      amount: payment.amount,
      tip_amount: payment.tipAmount,
      ...(payment.paidItems?.length ? { paid_items: payment.paidItems } : {}),
      ...(payment.payByUserName ? { pay_by_user_name: payment.payByUserName } : {}),
    },
    { headers: authHeaders(token) }
  );
  return response.data;
};

const webPaymentQuery = (pending) => ({
  d: pending.token,
  sig: pending.signature,
  ...(pending.sessionId ? { session_id: pending.sessionId } : {}),
});

/** How the payment went: { status, reason, amount, tip_amount, payment_method, card_last4, ... }. */
export const getWebPaymentStatus = async (pending, token) => {
  const response = await axios.get(
    `${BFF_URL}/api/opm/restaurants/${pending.restaurantId}/dining-sessions/${pending.diningSessionId}/web-payments/${pending.uniqueId}`,
    { params: webPaymentQuery(pending), headers: authHeaders(token) }
  );
  return response.data;
};

/** The guest left the page without paying. */
export const cancelWebPayment = async (pending, token) => {
  const response = await axios.delete(
    `${BFF_URL}/api/opm/restaurants/${pending.restaurantId}/dining-sessions/${pending.diningSessionId}/web-payments/${pending.uniqueId}`,
    { params: webPaymentQuery(pending), headers: authHeaders(token) }
  );
  return response.data;
};

/** Ask the menu assistant. Resolves to `{ text, dish_ids, action }`. */
export const askAssistant = async ({ messages, items, restaurantName, currency, diners }) => {
  const response = await axios.post(`${BFF_URL}/api/assistant`, {
    messages,
    items,
    restaurantName,
    currency,
    diners,
  });
  return response.data;
};
